import { getConfig } from '@config/env';

// Minimal Google Maps types (keep narrow to avoid pulling full @types/google.maps)
type PlacesServiceStatus =
  | 'OK'
  | 'ZERO_RESULTS'
  | 'OVER_QUERY_LIMIT'
  | 'REQUEST_DENIED'
  | 'INVALID_REQUEST'
  | 'UNKNOWN_ERROR';

type GeocoderStatus = PlacesServiceStatus;

interface GoogleAddressComponentRaw {
  long_name: string;
  short_name?: string;
  types: string[];
}

interface GoogleGeocoderResultRaw {
  formatted_address?: string;
  place_id?: string;
  address_components?: GoogleAddressComponentRaw[];
}

interface GeocoderComponentRestrictions {
  country: string;
  postalCode?: string;
}

interface Geocoder {
  geocode(
    request: {
      address: string;
      region?: string;
      componentRestrictions?: GeocoderComponentRestrictions;
    },
    callback: (results: GoogleGeocoderResultRaw[] | null, status: GeocoderStatus) => void
  ): void;
}

interface GoogleMapsNamespace {
  maps: {
    Geocoder: new () => Geocoder;
  };
}

declare global {
  interface Window {
    google?: GoogleMapsNamespace;
    __iehubGoogleMapsInit?: () => void;
  }
}

export interface GoogleAddressComponents {
  streetNumber?: string;
  route?: string;
  locality?: string;
  postalTown?: string;
  administrativeAreaLevel1?: string;
  administrativeAreaLevel2?: string;
  neighborhood?: string;
  sublocality?: string;
  country?: string;
  postalCode?: string;
  postalCodePrefix?: string;
  postalCodeSuffix?: string;
  premise?: string;
  subpremise?: string;
}

export interface GoogleGeocodeResult {
  formattedAddress?: string;
  addressComponents: GoogleAddressComponents;
  placeId?: string;
}

/**
 * Enriched address data ready for backend submission
 * Note: Backend CustomerModel supports these fields:
 * buildingNumber, buildingName, streetName, postTown, postCode, country, state
 * (city is NOT supported - use postTown instead)
 */
export interface EnrichedAddressData {
  postCode: string;
  buildingNumber?: string;
  streetName?: string;
  postTown?: string;
  state?: string;
  country?: string;
  buildingName?: string;
}

/**
 * Build enriched address data from Google geocode result.
 * If no Google result, returns minimum data with just postcode and building number.
 * IMPORTANT: Always sets all address fields (empty string if not available) to ensure
 * old values are cleared when user changes postcode.
 * @param postcode - Normalized postcode (no spaces, uppercase)
 * @param buildingNumber - Building/house number
 * @param googleResult - Result from Google Geocoder (can be null)
 * @returns Enriched address data for backend
 */
export function buildEnrichedAddressData(
  postcode: string,
  buildingNumber?: string,
  googleResult?: GoogleGeocodeResult | null
): EnrichedAddressData {
  const data: EnrichedAddressData = {
    postCode: postcode,
    // Always set building number (empty string if not provided)
    buildingNumber: buildingNumber?.trim() || "",
    // Always set all Google-enriched fields to ensure old values are cleared
    // when user changes postcode to one that doesn't return these fields
    streetName: "",
    postTown: "",
    state: "",
    country: "",
    buildingName: "",
  };

  if (googleResult) {
    const components = googleResult.addressComponents;

    // Set streetName from route
    if (components.route?.trim()) {
      data.streetName = components.route.trim();
    }

    // Use postalTown first, fall back to locality
    // Note: 'city' is NOT supported by backend - postTown is used for city/town
    if (components.postalTown?.trim()) {
      data.postTown = components.postalTown.trim();
    } else if (components.locality?.trim()) {
      data.postTown = components.locality.trim();
    }

    // Set state from administrative area level 1
    if (components.administrativeAreaLevel1?.trim()) {
      data.state = components.administrativeAreaLevel1.trim();
    }

    // Set country
    if (components.country?.trim()) {
      data.country = components.country.trim();
    }

    // Set building name from premise
    if (components.premise?.trim()) {
      data.buildingName = components.premise.trim();
    }
  }

  return data;
}

/**
 * Get address from Google with a timeout.
 * @param postcode - UK postcode
 * @param buildingNumber - Building/house number
 * @param timeoutMs - Timeout in milliseconds (default: 180000 = 3 minutes)
 * @returns Promise with address details or null if not found/timeout
 */
export async function getAddressFromGooglePlacesWithTimeout(
  postcode: string,
  buildingNumber?: string,
  timeoutMs: number = 180000
): Promise<GoogleGeocodeResult | null> {
  const timeoutPromise = new Promise<null>((resolve) => {
    setTimeout(() => {
      console.warn(`🔎 getAddressFromGooglePlaces: timeout after ${timeoutMs}ms`);
      resolve(null);
    }, timeoutMs);
  });

  const geocodePromise = getAddressFromGooglePlaces(postcode, buildingNumber);

  return Promise.race([geocodePromise, timeoutPromise]);
}

/**
 * Get full address details from Google Maps Geocoder using postcode and (optional) building number.
 * We intentionally avoid `google.maps.places.PlacesService` to prevent legacy Places warnings and
 * to keep this lookup lightweight.
 * @param postcode - UK postcode
 * @param buildingNumber - Building/house number
 * @returns Promise with address details or null if not found
 */
export async function getAddressFromGooglePlaces(
  postcode: string,
  buildingNumber?: string
): Promise<GoogleGeocodeResult | null> {
  const config = getConfig();
  const apiKey = config.services.maps.apiKey;

  if (!apiKey) {
    console.warn('Google Maps API key not configured');
    return null;
  }

  if (!postcode || postcode.trim().length === 0) {
    return null;
  }

  try {
    const normalizedPostcode = normalizeUkPostcode(postcode);
    const building = buildingNumber?.trim() ? buildingNumber.trim() : '';
    const normalizedBuilding = building ? building.toLowerCase() : null;
    const queries = buildQueryCandidates(normalizedPostcode, building);

    console.info('🔎 getAddressFromGooglePlaces: start', {
      query: queries[0] ?? '',
      postcode: normalizedPostcode,
      buildingNumber: building,
    });

    const googleMaps = await loadGoogleMapsSdk(apiKey);
    const geocoder = new googleMaps.maps.Geocoder();
    for (const query of queries) {
      const geocodeResults = await runGeocode(geocoder, query);
      if (geocodeResults.length === 0) {
        continue;
      }

      console.info('🔎 getAddressFromGooglePlaces: geocoder raw', {
        query,
        count: geocodeResults.length,
        sample: geocodeResults
          .slice(0, 5)
          .map(({ formatted_address, place_id, address_components }) => ({
            formatted_address,
            place_id,
            address_components,
          })),
      });

      const bestGeocode = selectBestResult(geocodeResults, normalizedBuilding);
      const result = bestGeocode ? buildGeocodeResult(bestGeocode) : null;
      console.info('🔎 getAddressFromGooglePlaces: geocoder result (selected)', result);
      if (result) {
        return result;
      }
    }

    console.info('🔎 getAddressFromGooglePlaces: no results', {
      query: queries[0] ?? '',
      queries,
    });
    return null;
  } catch (error) {
    console.error('Error calling Google Maps Geocoder:', error);
    return null;
  }
}

const mapAddressComponents = (
  components: GoogleAddressComponentRaw[] | undefined
): GoogleAddressComponents => {
  const addressComponents: GoogleAddressComponents = {};

  if (!components) {
    return addressComponents;
  }

  components.forEach((component) => {
    const types = component.types;

    if (types.includes('street_number')) {
      addressComponents.streetNumber = component.long_name;
    }
    if (types.includes('route')) {
      addressComponents.route = component.long_name;
    }
    if (types.includes('locality')) {
      addressComponents.locality = component.long_name;
    }
    if (types.includes('postal_town')) {
      addressComponents.postalTown = component.long_name;
    }
    if (types.includes('administrative_area_level_1')) {
      addressComponents.administrativeAreaLevel1 = component.long_name;
    }
    if (types.includes('administrative_area_level_2')) {
      addressComponents.administrativeAreaLevel2 = component.long_name;
    }
    if (types.includes('neighborhood')) {
      addressComponents.neighborhood = component.long_name;
    }
    if (types.includes('sublocality') || types.includes('sublocality_level_1')) {
      addressComponents.sublocality = component.long_name;
    }
    if (types.includes('country')) {
      addressComponents.country = component.long_name;
    }
    // UK postcodes sometimes come back as "postal_code_prefix" (e.g. "BB1") when geocoding
    // with postcode-only queries. Keep prefix separate so we don't overwrite a full user-entered
    // postcode like "BB1 1AA".
    if (types.includes('postal_code_prefix')) {
      addressComponents.postalCodePrefix = component.long_name;
    } else if (types.includes('postal_code')) {
      addressComponents.postalCode = component.long_name;
    }
    if (types.includes('postal_code_suffix')) {
      addressComponents.postalCodeSuffix = component.long_name;
    }
    if (types.includes('premise')) {
      addressComponents.premise = component.long_name;
    }
    if (types.includes('subpremise')) {
      addressComponents.subpremise = component.long_name;
    }
  });

  return addressComponents;
};

const buildGeocodeResult = (
  result: GoogleGeocoderResultRaw
): GoogleGeocodeResult => ({
  formattedAddress: result.formatted_address,
  addressComponents: mapAddressComponents(result.address_components),
  placeId: result.place_id,
});

const selectBestResult = <T extends GoogleGeocoderResultRaw>(
  results: T[],
  normalizedBuilding: string | null
): T | null => {
  if (results.length === 0) {
    return null;
  }

  if (!normalizedBuilding) {
    return results[0];
  }

  const match = results.find((candidate) =>
    matchesBuildingNumber(candidate.address_components, normalizedBuilding)
  );

  return match ?? results[0];
};

const matchesBuildingNumber = (
  components: GoogleAddressComponentRaw[] | undefined,
  normalizedBuilding: string
): boolean => {
  if (!components || !normalizedBuilding) {
    return false;
  }

  return components.some((component) => {
    if (!component?.long_name) return false;
    const value = component.long_name.trim().toLowerCase();
    if (!value) return false;

    const typeMatches =
      component.types.includes('street_number') ||
      component.types.includes('premise') ||
      component.types.includes('subpremise');

    return typeMatches && value === normalizedBuilding;
  });
};

const runGeocode = (geocoder: Geocoder, query: string): Promise<GoogleGeocoderResultRaw[]> =>
  new Promise((resolve, reject) => {
    geocoder.geocode(
      {
        address: query,
        region: 'gb',
        componentRestrictions: { country: 'GB' },
      },
      (results, status: GeocoderStatus) => {
        if (status === 'OK' && results) {
          resolve(results);
          return;
        }

        if (status === 'ZERO_RESULTS') {
          resolve([]);
          return;
        }

        reject(new Error(`Geocoder failed with status ${status}`));
      }
    );
  });

let googleMapsLoader: Promise<GoogleMapsNamespace> | null = null;

const loadGoogleMapsSdk = (apiKey: string): Promise<GoogleMapsNamespace> => {
  if (googleMapsLoader) {
    return googleMapsLoader;
  }

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(
      new Error('Google Maps SDK is only available in browser environments')
    );
  }

  // Reuse loaded SDK if present
  if (window.google?.maps?.Geocoder) {
    return Promise.resolve(window.google);
  }

  googleMapsLoader = new Promise((resolve, reject) => {
    const callbackName: '__iehubGoogleMapsInit' = '__iehubGoogleMapsInit';
    const timeoutMs = 15000;
    let timeoutId: number | null = null;

    const cleanup = () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
      // best-effort cleanup; do not delete window.google
      if (window.__iehubGoogleMapsInit) {
        delete window.__iehubGoogleMapsInit;
      }
    };

    const fail = (err: Error) => {
      cleanup();
      // allow retry after a failure
      googleMapsLoader = null;
      reject(err);
    };

    const resolveWhenReady = () => {
      waitForGoogleGeocoder()
        .then(() => {
          if (window.google?.maps?.Geocoder) {
            resolve(window.google);
            cleanup();
          } else {
            fail(new Error('Google Maps SDK loaded but Geocoder is unavailable'));
          }
        })
        .catch((e: unknown) => {
          fail(e instanceof Error ? e : new Error('Failed waiting for Google Maps SDK'));
        });
    };

    // Google-recommended async pattern uses callback=... for readiness
    window.__iehubGoogleMapsInit = resolveWhenReady;

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      apiKey
    )}&v=weekly&loading=async&callback=${encodeURIComponent(callbackName)}`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      fail(new Error('Failed to load Google Maps SDK'));
    };

    document.head.appendChild(script);

    timeoutId = window.setTimeout(() => {
      fail(new Error(`Google Maps SDK did not initialize within ${timeoutMs}ms`));
    }, timeoutMs);
  });

  return googleMapsLoader;
};

const waitForGoogleGeocoder = (): Promise<void> =>
  new Promise((resolve, reject) => {
    const startedAt = Date.now();
    const timeoutMs = 15000;

    const tick = () => {
      if (window.google?.maps?.Geocoder) {
        resolve();
        return;
      }

      if (Date.now() - startedAt > timeoutMs) {
        reject(new Error('Timed out waiting for google.maps.Geocoder'));
        return;
      }

      window.setTimeout(tick, 25);
    };

    tick();
  });

const normalizeUkPostcode = (value: string): string => {
  const compact = value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (compact.length <= 3) {
    return compact;
  }
  return `${compact.slice(0, -3)} ${compact.slice(-3)}`;
};

const looksLikeHouseIdentifier = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed) return false;

  // Pure numeric house numbers: keep reasonable length (avoid obviously invalid inputs)
  if (/^\d+$/.test(trimmed)) {
    return trimmed.length <= 5;
  }

  // Alphanumeric identifiers like "10A", "1/2", "Flat 2" are allowed but bounded
  return trimmed.length <= 20;
};

const buildQueryCandidates = (normalizedPostcode: string, building: string): string[] => {
  const queries: string[] = [];
  const postcode = normalizedPostcode.trim();
  const buildingTrimmed = building.trim();

  if (postcode) {
    if (buildingTrimmed && looksLikeHouseIdentifier(buildingTrimmed)) {
      queries.push(`${buildingTrimmed} ${postcode}`);
    }
    queries.push(postcode);
    // Sometimes adding country helps with short/ambiguous inputs
    queries.push(`${postcode} UK`);
  }

  // De-dupe while preserving order
  return queries.filter((q, idx) => queries.indexOf(q) === idx);
};
